import { format, isAfter, isSameDay, startOfDay } from 'date-fns';
import { Clapperboard, ClipboardList, Monitor, Pencil, Trash2, Trophy } from 'lucide-react';
import { Game, GameTeam } from 'modules/games/domain/interfaces/game';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';

import { Team } from 'modules/teams/domain/interfaces/team';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { useReactToPrint } from 'react-to-print';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import EmptyButton from 'ud-ui/components/empty-button';
import { useModal } from 'ud-ui/components/modal-old/use-modal';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import { DivProps } from '../../../../../typings';
import { GameDetails } from '../../../domain/interfaces/game-details';
import { fetchGameDetails } from '../../../store/actions';
import { MatchProtocolToPrint } from './match-protocol-to-print';
import * as S from './styles';

type GameTeamProps = {
  team: GameTeam | null;
  className?: string;
};

const GameTeamInfo = ({ team, className }: GameTeamProps) => {
  const isLeft = className === 'left';
  const iconFirst = !isLeft;
  const hasScore = !!team && team.score !== null;
  const infoNode = <TeamInfo team={team} iconFirst={iconFirst} />;

  return (
    <S.TeamContainer className={className}>
      {isLeft && infoNode}
      {hasScore && <S.GameTeamScore className="score">{team?.score}</S.GameTeamScore>}
      {!isLeft && infoNode}
    </S.TeamContainer>
  );
};

type TeamInfoProps = {
  team: Team | null;
  iconFirst?: boolean;
};

const defaultTeamInfoProps = {
  iconFirst: true,
};

const TeamInfo = (props: TeamInfoProps) => {
  const allProps = {
    ...defaultTeamInfoProps,
    ...props,
  };

  const { iconFirst, team } = allProps;

  const teamName = team?.name || 'Неизвестно';
  const teamLogo = team?.logo?.url;

  if (iconFirst) {
    return (
      <>
        <TeamLogo className="mr-2" url={teamLogo} />
        <UDText type="subhead">{teamName}</UDText>
      </>
    );
  }

  return (
    <>
      <UDText type="subhead">{teamName}</UDText>
      <TeamLogo className="ml-2" url={teamLogo} />
    </>
  );
};

const TeamsDelimiter = () => (
  <S.TeamsDelimiterContainer>
    <S.TeamsDelimiter />
  </S.TeamsDelimiterContainer>
);

type GamesComponentsGameCardProps = {
  game: Game;
  championshipId: number;
  onEditClick?: (game: Game) => void;
  onShowTemplatesClick?: (gameId: number) => void;
  onShowResultClick?: (gameId: number) => void;
  onDeleteClick?: (gameId: number) => void;
  onShowMomentsClicked?: (gameId: number) => void;
} & Omit<DivProps, 'children'>;

const GamesComponentsGameCard = (props: GamesComponentsGameCardProps) => {
  const {
    game,
    championshipId,
    onEditClick,
    onShowResultClick,
    onShowTemplatesClick,
    onDeleteClick,
    onShowMomentsClicked,
    ...containerProps
  } = props;

  const { info } = useOrganizer();

  const { open: openDeleteModal, close: closeDeleteModal, Modal: ConfirmDeleteModal } = useModal({ defaultShow: false });

  const dispatch = useDispatch<AppDispatch>();

  const printRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const promiseResolveRef = useRef<((value: void | PromiseLike<void>) => void) | null>(null);
  const [isPrintLoading, setPrintLoading] = useState<boolean>(false);

  const [matchDetails, setMatchDetails] = useState<GameDetails | null>(null);

  const { data: availableFeatures, isFetching: isFeatureFetching } = useFetchAvailableFeature();

  const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
  const showTemplatesBtn = useMemo(
    () => (isDev ? true : (availableFeatures?.OBS_INTEGRATION ?? false)),
    [isDev, availableFeatures?.OBS_INTEGRATION],
  );

  const showEditResultsBtn = useMemo(() => {
    const now = new Date();

    return game.hasResults || isSameDay(new Date(game.date), now) || isAfter(startOfDay(now), new Date(game.date));
  }, [game]);

  const showMomentsBtn = useMemo(() => {
    // const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
    return showEditResultsBtn && onShowMomentsClicked;
  }, [onShowMomentsClicked, showEditResultsBtn]);

  const onShowResultClicked = useCallback(() => {
    if (onShowResultClick) {
      onShowResultClick(game.id);
    }
  }, [game, onShowResultClick]);

  const onShowTemplatesClicked = useCallback(() => {
    if (onShowTemplatesClick) {
      onShowTemplatesClick(game.id);
    }
  }, [game, onShowTemplatesClick]);

  const handleMomentsClick = useCallback(() => {
    if (onShowMomentsClicked) {
      onShowMomentsClicked(game.id);
    }
  }, [game.id, onShowMomentsClicked]);

  const deleteClicked = useCallback(() => {
    openDeleteModal();
  }, [openDeleteModal]);

  const cancelDeleteEvent = useCallback(() => {
    closeDeleteModal();
  }, [closeDeleteModal]);

  const confirmDeleteEvent = useCallback(() => {
    if (onDeleteClick) {
      onDeleteClick(game.id);
    }
    closeDeleteModal();
  }, [game, onDeleteClick, closeDeleteModal]);

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const print = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Протокол матча',
    onBeforePrint: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      setPrintLoading(false);
      setMatchDetails(null);

      setIsPrinting(false);
      promiseResolveRef.current = null;
    },
  });

  const handlePrint = useCallback(() => {
    setPrintLoading(true);
    dispatch(
      fetchGameDetails({
        gameId: game.id,
        championshipId,
      }),
    )
      .unwrap()
      .then((result) => {
        setMatchDetails(result as GameDetails);
        setTimeout(() => {
          print();
        }, 0);
      });
  }, [championshipId, dispatch, game.id, print]);

  const gameTeams = game.teams.filter(Boolean);

  return (
    <S.GameCardContainer {...containerProps}>
      <S.GameCardTime>
        {props.game.date && !isNaN(new Date(props.game.date).getTime()) && new Date(props.game.date).getTime() > 0
          ? format(new Date(props.game.date), 'kk:mm')
          : '--:--'}
      </S.GameCardTime>

      <S.GameCardTeams>
        <GameTeamInfo className="left" team={gameTeams[0]} />
        <TeamsDelimiter />
        <GameTeamInfo className="right" team={gameTeams[1]} />
      </S.GameCardTeams>

      <S.GameActions>
        <EmptyButton
          className="game-action-btn"
          onClick={onShowTemplatesClicked}
          icon={<Monitor size={18} />}
          disabled={isFeatureFetching || !showTemplatesBtn}
        />

        {showMomentsBtn && (
          <S.IconButton className="game-action-btn" onClick={handleMomentsClick}>
            <Clapperboard size={18} />
          </S.IconButton>
        )}

        <S.IconButton className="game-action-btn" title="Протокол матча" onClick={() => handlePrint()} disabled={isPrintLoading}>
          {isPrintLoading ? <ClipLoader size={18} /> : <ClipboardList size={18} />}
        </S.IconButton>

        {showEditResultsBtn && (
          <S.IconButton className="game-action-btn" onClick={onShowResultClicked}>
            <Trophy size={18} />
          </S.IconButton>
        )}

        {onEditClick && (
          <S.IconButton className="game-action-btn" onClick={() => onEditClick(game)}>
            <Pencil size={18} />
          </S.IconButton>
        )}

        {onDeleteClick && (
          <S.IconButton className="game-action-btn" onClick={deleteClicked} color={theme.colors.CaseError}>
            <Trash2 size={18} />
          </S.IconButton>
        )}
      </S.GameActions>

      <ConfirmDeleteModal
        header={{
          title: 'Подтвердить удаление',
          subtitle: 'Вы действительно хотите удалить матч?',
        }}
      >
        <div className="mt-10 flex justify-between">
          <UDButton variant="secondary" className="mb-3" type="button" onClick={cancelDeleteEvent}>
            Отмена
          </UDButton>

          <UDButton variant="primary" className="mb-3" type="button" onClick={confirmDeleteEvent}>
            Удалить
          </UDButton>
        </div>
      </ConfirmDeleteModal>
      <div style={{ display: 'none' }}>
        <MatchProtocolToPrint logoUrl={info?.logoUrl} matchDetails={matchDetails} ref={printRef} />
      </div>
    </S.GameCardContainer>
  );
};

export default GamesComponentsGameCard;
